/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as React from 'react'
import { useEffect, useState } from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import TextField from '@material-ui/core/TextField/TextField'

import {
  Grid,
  FormControlLabel,
  Typography,
  Chip,
  withStyles,
  Theme,
  createStyles,
  WithStyles
} from '@material-ui/core'

import { debounce } from 'debounce'
import { CONFIG } from '../../config'
import { IPerson } from '../../models/Person'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../OneplaceLibraryProvider'

const styles = (_theme: Theme) =>
  createStyles({
    root: {},
    label: {
      fontWeight: 'bold'
    },
    peopleLabel: {
      fontWeight: 'normal'
    },
    mandatoryField: {
      color: _theme.palette.secondary.main
    }
  })
const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

export interface IPeopleControlProps
  extends WithStyles<typeof styles>,
    IOneplaceLibraryContextProp {
  selectedPeople: IPerson[]
  hideHardAssociation?: boolean
  hardAssociation: boolean
  handlePeopleChange: (event: any, newValue: IPerson[]) => void
  handlePeopleHardAssociationChange: (event: any) => void
  required?: boolean
  queryPeople: (keyword: string) => Promise<IPerson[]>
  offlineMode: boolean
  currentId: number // incident type id, checklist template id or ticket category id
  disabled?: boolean
}

const PeopleControl = withOneplaceLibraryContext(
  withStyles(styles)((props: IPeopleControlProps) => {
    const [peopleOptions, setPeopleOptions] = useState<IPerson[]>([])
    const [loading, setLoading] = useState(false)
    const [hasUserSelected, setHasUserSelected] = useState(false)

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const refreshPeopleOptions = debounce(async (keyword: string) => {
      setLoading(true)
      try {
        const response = await props.queryPeople(keyword)
        setPeopleOptions(response)
      } catch (error) {
        console.error('Error fetching people:', error)
      } finally {
        setLoading(false)
      }
    }, CONFIG.searchDelay)

    const loadInitialPeopleOptions = async () => {
      setLoading(true)
      try {
        const response = await props.queryPeople(' ')
        setPeopleOptions(response)
      } catch (error) {
        console.error('Error fetching people:', error)
      } finally {
        setLoading(false)
      }
    }

    const checkUserSelection = () => {
      const hasUser = props.selectedPeople.findIndex((p) => p.user) !== -1
      setHasUserSelected(hasUser)
    }

    // when selected people changes, check if we need to set the 'hasUserSelected'
    useEffect(() => {
      checkUserSelection()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedPeople])

    useEffect(() => {
      if (props.currentId) {
        // both online and offline mode: refresh people list if the incident type or ticket category changes
        void loadInitialPeopleOptions()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.currentId])

    const handleInputChange = (_event: any, newInputValue: string) => {
      // offline mode loads all matching options into list
      if (!props.offlineMode && newInputValue) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        refreshPeopleOptions(newInputValue)
      }
    }

    const t = props.ctx.i18next.t

    return (
      <>
        <Grid item xs={12}>
          <Autocomplete
            onChange={props.handlePeopleChange}
            onInputChange={handleInputChange}
            multiple
            id='people-field'
            options={peopleOptions}
            loading={loading}
            getOptionDisabled={(option: IPerson) =>
              option.status === 'archived'
            }
            getOptionSelected={(option: IPerson, value: IPerson) =>
              option.id === value.id
            }
            value={props.selectedPeople}
            disabled={props.disabled}
            getOptionLabel={(p) => p.name}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  key={index}
                  classes={{
                    label: option.user
                      ? props.classes.label
                      : props.classes.peopleLabel
                  }}
                  label={option.name}
                  {...getTagProps({ index })}
                  disabled={option.status === 'archived' || props.disabled}
                />
              ))
            }
            renderOption={(option, { selected }) => (
              <React.Fragment>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  color='primary'
                />
                {option.user ? (
                  <Typography style={{ fontWeight: 'bold', margin: '1px' }}>
                    {option.name}
                  </Typography>
                ) : (
                  <Typography>{option.name}</Typography>
                )}
              </React.Fragment>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant='standard'
                label={t('customLabel_people')}
                InputLabelProps={{
                  className: props.required ? props.classes.mandatoryField : '',
                  shrink: true,
                  disabled: props.disabled
                }}
                placeholder=''
              />
            )}
          />
        </Grid>
        {!props.hideHardAssociation && hasUserSelected && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.hardAssociation || false}
                  onChange={props.handlePeopleHardAssociationChange}
                  name='hardAssociation'
                  color='primary'
                />
              }
              label={`Restrict to ${t('customLabel_people')}`}
            />
          </Grid>
        )}
      </>
    )
  })
)

export default PeopleControl
